<script lang="ts" setup>
import { reactive, computed, onMounted } from 'vue';
import { CdrInput, CdrButton } from '@rei/cedar';
import { VALIDATION_PATTERNS } from '@rei/constants';
import { recordClick } from '@utils/analytics.mts';
import {
  ERROR_VALID_ORDER_NUMBER,
  ERROR_VALID_ZIP_CODE,
  ERROR_LAST_NAME,
} from '@utils/constants.mts';
import type { OrderLookupFormField, OrderLookupFormProps, OrderLookupFormState } from '@/types/index.mts';

const props = defineProps<OrderLookupFormProps>();

const state: OrderLookupFormState = reactive({
  formValues: {
    orderNumber: '',
    lastName: '',
    zipCode: '',
  },
  formErrors: {
    orderNumber: false,
    lastName: false,
    zipCode: false,
  },
  analyticsErrors: [],
  backgroundColor: 'primary',
});

const orderLookupPage = computed(() => props.containerPage === 'order-lookup');

const validateField = (fieldName: OrderLookupFormField) => {
  switch (fieldName) {
    case 'orderNumber':
      state.formErrors.orderNumber = !/^([A|Y|C|B|a|y|c|b]\d{9})$/.test(
        state.formValues.orderNumber,
      );
      if (state.formErrors.orderNumber) {
        state.analyticsErrors.push('Order number: This field is required');
      }
      break;
    case 'zipCode':
      state.formErrors.zipCode = !VALIDATION_PATTERNS.ZIPCODE.test(state.formValues.zipCode);
      if (state.formErrors.zipCode) {
        state.analyticsErrors.push('Billing address Zip code: This field is required');
      }
      break;
    case 'lastName':
      state.formErrors.lastName = state.formValues.lastName.trim() === '';
      if (state.formErrors.lastName) {
        state.analyticsErrors.push('Last name on billing address: This field is required');
      }
      break;
    default:
      break;
  }
};

const validateForm = () => {
  state.analyticsErrors = [];

  validateField('orderNumber');
  validateField('zipCode');
  validateField('lastName');

  if (state.formErrors.orderNumber || state.formErrors.zipCode || state.formErrors.lastName) {
    const errorCodes = state.analyticsErrors.join(', ');
    recordClick({ errorCodes });

    return;
  }

  const { orderNumber, lastName, zipCode } = state.formValues;
  const url = `/purchase-history/purchase-details?orderId=${orderNumber.toUpperCase()}&lastName=${lastName}&zipCode=${zipCode}`;

  window.location.href = url;
};

const handleInput = (event: InputEvent, fieldName: OrderLookupFormField) => {
  const { value } = event.target as HTMLInputElement;

  state.formValues[fieldName] = value;

  if (state.formErrors[fieldName]) {
    validateField(fieldName);
  }
};

onMounted(() => {
  if (props.orderId) {
    state.formValues.orderNumber = props.orderId;
  }
});
</script>

<template>
  <section class="order-lookup-form">
    <form
      method="get"
      data-ui="order-tracking-form"
      @submit.prevent="validateForm"
    >
      <fieldset>
        <slot />
        <div class="order-lookup-form__form-element">
          <cdr-input
            v-model="state.formValues.orderNumber"
            label="Order number"
            required
            name="orderNumber"
            aria-describedby="errorMessageOrderNumber"
            data-ui="input-purchasenum"
            :background="state.backgroundColor"
            :error="state.formErrors.orderNumber"
            @blur="validateField('orderNumber')"
            @input="handleInput($event, 'orderNumber')"
          >
            <template #helper-text-bottom>
              Starts with "A" or "Y" followed by a 9-digit code
            </template>
            <template #error>
              <span id="errorMessageOrderNumber">{{ ERROR_VALID_ORDER_NUMBER }}</span>
            </template>
          </cdr-input>
        </div>
        <div class="order-lookup-form__form-element">
          <cdr-input
            v-model="state.formValues.lastName"
            label="Last name on billing address"
            required
            name="lastName"
            aria-describedby="errorMessageLastName"
            data-ui="input-lastname"
            :background="state.backgroundColor"
            :error="state.formErrors.lastName"
            @blur="validateField('lastName')"
            @input="handleInput($event, 'lastName')"
          >
            <template #error>
              <span id="errorMessageLastName">{{ ERROR_LAST_NAME }}</span>
            </template>
          </cdr-input>
        </div>
        <div class="order-lookup-form__form-element">
          <cdr-input
            v-model="state.formValues.zipCode"
            label="Billing address ZIP code"
            required
            name="zipCode"
            aria-describedby="errorMessageZipCode"
            data-ui="input-zip"
            :background="state.backgroundColor"
            :error="state.formErrors.zipCode"
            @blur="validateField('zipCode')"
            @input="handleInput($event, 'zipCode')"
          >
            <template #helper-text-bottom>
              International customers: Enter "NA" if no postal code. REI employees: You may need to
              use headquarters ZIP code "98390"
            </template>
            <template #error>
              <span id="errorMessageZipCode">{{ ERROR_VALID_ZIP_CODE }}</span>
            </template>
          </cdr-input>
        </div>
        <div class="order-lookup-form__form-element">
          <cdr-button
            type="submit"
            :data-analytics-id="`${
              orderLookupPage ? 'order tracking_unregistered' : 'purchase history'
            }:Find my Order`"
            data-ui="button-submit-tracking"
            :full-width="true"
            :modifier="orderLookupPage ? 'secondary' : 'primary'"
          >
            {{ orderLookupPage ? 'Find order' : 'Find my order' }}
          </cdr-button>
        </div>
      </fieldset>
    </form>
  </section>
</template>

<style lang="scss" scoped>
fieldset {
  padding: 0;
  margin: 0;
  border: none;
}

.order-lookup-form {
  @include cdr-text-body-strong-300;

  display: grid;
  grid-template-columns: 1fr;

  &__form-element {
    margin-bottom: $cdr-space-one-and-a-half-x;
  }

  &__required-msg {
    @include cdr-text-body-300;

    font-style: italic;
  }
}
</style>
